import { observer } from 'mobx-react-lite';
import { FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useThemeContext } from '../../../global-state/themeContext';
import { SortDirection } from '../../../global-state/types';
import { CustomerBaseValueViewItem, CustomerBaseValueViewResponse } from '../../../hooks';
import { useFilteredDataByArea } from '../../../hooks/useFilteredData';
import { Typography } from '../../../stories/atoms';
import { ColumnFlexContainer } from '../../../stories/atoms/containerV2/ContainerV2';
import { Animation, Animations } from '../../../stories/molecules';
import { HeaderRowItem, Table } from '../../../stories/molecules/tablev2/Table';
import { muiTheme } from '../../../theme';
import { commonTranslations, homeTranslations } from '../../../translations';
import { areaResultTranslations } from '../../../translations/areaResult';
import { formatAmountWithSeparator } from '../../../utils';
import { AreaResultHeaderTooltip, SummaryType } from '../areaResultHeaderTooltip/AreaResultHeaderTooltip';

export type CustomerBaseValueViewProps = {
  isLoading: boolean;
  isError: boolean;
  storesByArea: string[];
  customerData: CustomerBaseValueViewResponse | null;
  selectedStores: string[];
};

enum CustomerBaseValueColumnType {
  StoreId = 'storeId',
  ActiveCustomers = 'activeCustomers',
  RecruitedCustomers = 'recruitedCustomers',
  Unrecruited = 'unrecruitedCustomers',
  RecruitedShare = 'recruitedShare',
  MemberSoR = 'memberShareOfReceipt',
  AppCustomers = 'appCustomers',
}

export const CustomerBaseValueView: FC<CustomerBaseValueViewProps> = observer(
  ({ isLoading, isError, storesByArea, customerData, selectedStores }) => {
    const { t } = useTranslation();
    const { customerBaseValue } = areaResultTranslations;
    const { mode } = useThemeContext();

    const isDarkMode = mode === 'dark';
    const themeBorderColor = isDarkMode ? muiTheme.color.borderGrayDark : muiTheme.color.borderGray;
    const headerBorderBottomColor = isDarkMode ? muiTheme.color.primary : muiTheme.color.borderGray;
    const headerFooterSxProps = {
      padding: '12px 8px',
      ':nth-of-type(2),:nth-of-type(5),:nth-of-type(6)': {
        border: `1px solid ${themeBorderColor}`,
        borderLeft: 'none',
        borderTop: 'none',
        borderBottom: `1px solid ${headerBorderBottomColor}`,
      },
    };

    const [sortColumnId, setSortColumnId] = useState<CustomerBaseValueColumnType | null>(null);
    const [sortDirection, setSortDirection] = useState<SortDirection | null>(null);

    const filteredCustomerData = useFilteredDataByArea(
      customerData?.customerBaseValue,
      storesByArea,
      selectedStores,
    );

    const headerItems: HeaderRowItem<CustomerBaseValueColumnType>[] = [
      {
        id: CustomerBaseValueColumnType.StoreId,
        content: (
          <Typography>
            {t(areaResultTranslations.storeId.key, areaResultTranslations.storeId.defaultValue)}
          </Typography>
        ),
      },
      {
        id: CustomerBaseValueColumnType.ActiveCustomers,
        content: (
          <AreaResultHeaderTooltip
            isBold
            headerText={t(customerBaseValue.activeCustomers.key, customerBaseValue.activeCustomers.defaultValue)}
            tooltipText={t(
              homeTranslations.appCustomers.activeCustomersTooltip.key,
              homeTranslations.appCustomers.activeCustomersTooltip.defaultValue,
            )}
            summaryType={SummaryType.SUM}
          />
        ),
      },
      {
        id: CustomerBaseValueColumnType.RecruitedCustomers,
        content: (
          <ColumnFlexContainer>
            <Typography type="button">
              {t(homeTranslations.newMembers.title.key, homeTranslations.newMembers.title.defaultValue)}
            </Typography>
            <AreaResultHeaderTooltip
              headerText={t(
                customerBaseValue.recruitedCustomers.key,
                customerBaseValue.recruitedCustomers.defaultValue,
              )}
              tooltipText={t(
                homeTranslations.newMembers.recruitedTooltip.key,
                homeTranslations.newMembers.recruitedTooltip.defaultValue,
              )}
              summaryType={SummaryType.SUM}
            />
          </ColumnFlexContainer>
        ),
      },
      {
        id: CustomerBaseValueColumnType.Unrecruited,
        content: (
          <AreaResultHeaderTooltip
            headerText={t(customerBaseValue.unrecruited.key, customerBaseValue.unrecruited.defaultValue)}
            tooltipText={t(
              homeTranslations.newMembers.unRecruitedTooltip.key,
              homeTranslations.newMembers.unRecruitedTooltip.defaultValue,
            )}
            summaryType={SummaryType.SUM}
          />
        ),
      },
      {
        id: CustomerBaseValueColumnType.RecruitedShare,
        content: (
          <AreaResultHeaderTooltip
            headerText={t(customerBaseValue.recruitedShare.key, customerBaseValue.recruitedShare.defaultValue)}
            tooltipText={t(
              homeTranslations.newMembers.recruitedShareTooltip.key,
              homeTranslations.newMembers.recruitedShareTooltip.defaultValue,
            )}
            summaryType={SummaryType.GROSSAVG}
          />
        ),
      },
      {
        id: CustomerBaseValueColumnType.MemberSoR,
        content: (
          <AreaResultHeaderTooltip
            headerText={t(customerBaseValue.memberSoR.key, customerBaseValue.memberSoR.defaultValue)}
            tooltipText={t(
              homeTranslations.shareOfReceipts.toolTip.key,
              homeTranslations.shareOfReceipts.toolTip.defaultValue,
            )}
            summaryType={SummaryType.GROSSAVG}
            isBold
          />
        ),
      },
      {
        id: CustomerBaseValueColumnType.AppCustomers,
        content: (
          <AreaResultHeaderTooltip
            headerText={t(customerBaseValue.appCustomers.key, customerBaseValue.appCustomers.defaultValue)}
            tooltipText={t(
              homeTranslations.appCustomers.toolTip.key,
              homeTranslations.appCustomers.toolTip.defaultValue,
            )}
            summaryType={SummaryType.SUM}
            isBold
          />
        ),
      },
    ];

    const isNoData = useMemo(() => {
      return !customerData || !filteredCustomerData?.length;
    }, [customerData, filteredCustomerData, storesByArea]);

    const getTableItem = (item: CustomerBaseValueViewItem, isSummary: boolean) => {
      const typographyType = isSummary ? 'body3' : 'body2';
      return [
        {
          id: CustomerBaseValueColumnType.StoreId,
          width: '10%',
          content: (
            <Typography type={typographyType}>
              {isSummary
                ? t(areaResultTranslations.summary.key, areaResultTranslations.summary.defaultValue)
                : item.storeId}
            </Typography>
          ),
        },
        {
          id: CustomerBaseValueColumnType.ActiveCustomers,
          content: (
            <Typography type={typographyType}>{formatAmountWithSeparator(item.activeCustomers)}</Typography>
          ),
        },
        {
          id: CustomerBaseValueColumnType.RecruitedCustomers,
          content: (
            <Typography type={typographyType}>{formatAmountWithSeparator(item.recruitedCustomers)}</Typography>
          ),
        },
        {
          id: CustomerBaseValueColumnType.Unrecruited,
          content: (
            <Typography type={typographyType}>{formatAmountWithSeparator(item.unrecruitedCustomers)}</Typography>
          ),
        },
        {
          id: CustomerBaseValueColumnType.RecruitedShare,
          content: (
            <Typography type={typographyType}>{formatAmountWithSeparator(item.recruitedShare, '%')}</Typography>
          ),
        },
        {
          id: CustomerBaseValueColumnType.MemberSoR,
          content: (
            <Typography type={typographyType}>
              {formatAmountWithSeparator(item.memberShareOfReceipt, '%')}
            </Typography>
          ),
        },
        {
          id: CustomerBaseValueColumnType.AppCustomers,
          content: <Typography type={typographyType}>{formatAmountWithSeparator(item.appCustomers)}</Typography>,
        },
      ];
    };

    const getFooterItems = () => {
      if (selectedStores.length !== 0 || !customerData?.summary) {
        return [];
      }
      return getTableItem({ storeId: 'Summary', ...customerData?.summary }, true);
    };

    const onColumnSort = (columnType: CustomerBaseValueColumnType) => {
      if (columnType !== sortColumnId) {
        setSortColumnId(columnType);
        setSortDirection(SortDirection.Descending);
        return;
      }

      if (sortDirection === SortDirection.Ascending) {
        setSortColumnId(columnType);
        setSortDirection(SortDirection.Descending);
        return;
      }

      setSortColumnId(columnType);
      setSortDirection(SortDirection.Ascending);
    };

    return (
      <>
        {isLoading && (
          <ColumnFlexContainer flex={1} data-testid="customer-base-value-loading-view">
            <Animation
              title={t(customerBaseValue.loading.key, customerBaseValue.loading.defaultValue)}
              animation={Animations.LOADING}
            />
          </ColumnFlexContainer>
        )}

        {!isLoading && isError && (
          <ColumnFlexContainer flex={1} data-testid="customer-base-value-error-view">
            <Animation
              title={t(commonTranslations.errorText1.key, commonTranslations.errorText1.defaultValue)}
              subtitle={t(commonTranslations.errorText2.key, commonTranslations.errorText2.defaultValue)}
              animation={Animations.SAD}
            />
          </ColumnFlexContainer>
        )}

        {!isLoading && !isError && isNoData && (
          <ColumnFlexContainer flex={1} data-testid="customer-base-value-no-data-view">
            <Animation
              title={t(customerBaseValue.noData.key, customerBaseValue.noData.defaultValue)}
              animation={Animations.CONFUSED}
            />
          </ColumnFlexContainer>
        )}

        {!isLoading && !isError && !isNoData && (
          <ColumnFlexContainer flex={1} padding={[4, 0]} minHeight={[0]} data-testid="customer-base-value-table">
            <Table
              items={filteredCustomerData}
              headerItems={headerItems}
              stickyHeader
              stickyFirstColumn
              disableHover
              sortConfig={{
                storeId: { getItemValue: (item) => item.storeId },
                activeCustomers: { getItemValue: (item) => item.activeCustomers },
                recruitedCustomers: { getItemValue: (item) => item.recruitedCustomers },
                unrecruitedCustomers: { getItemValue: (item) => item.unrecruitedCustomers },
                recruitedShare: { getItemValue: (item) => item.recruitedShare },
                memberShareOfReceipt: { getItemValue: (item) => item.memberShareOfReceipt },
                appCustomers: { getItemValue: (item) => item.appCustomers },
              }}
              sortColumnId={sortColumnId || undefined}
              onSort={onColumnSort}
              sortDirection={sortDirection || undefined}
              sxProps={{
                padding: '12px 8px',
                ':nth-of-type(2),:nth-of-type(5),:nth-of-type(6)': {
                  border: `1px solid ${themeBorderColor}`,
                  borderLeft: 'none',
                  borderTop: 'none',
                },
                ':nth-of-type(3),:nth-of-type(4)': {
                  border: `1px dashed ${themeBorderColor}`,
                  borderLeft: 'none',
                  borderTop: 'none',
                  borderBottom: `1px solid ${themeBorderColor}`,
                },
              }}
              headerSxProps={headerFooterSxProps}
              footerSxProps={headerFooterSxProps}
              getRowItems={(item) => {
                return getTableItem(item, false);
              }}
              footerItems={getFooterItems()}
            />
          </ColumnFlexContainer>
        )}
      </>
    );
  },
);
